import { useState } from "react";

import { AlertTriangle, Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "~/components/ui/tabs";

import { AddTotemDialog } from "./components/AddTotemDialog";
import { BucketTotemsTab } from "./components/BucketTotemsTab";
import { ClientTotemsTab } from "./components/ClientTotemsTab";
import { MovementsHistoryTab } from "./components/MovementsHistoryTab";
import { PendingAssignmentsTab } from "./components/PendingAssignmentsTab";
import { TotemStatsCards } from "./components/TotemStatsCards";
import { usePendingAssignmentsCount } from "./hooks/usePendingAssignments";
import { useTotemStats, useTotems } from "./hooks/useTotems";

export default function TotemsPage() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [tab, setTab] = useState("por-estado");

  const { data: stats, isLoading: statsLoading } = useTotemStats();
  const { data: allTotems, isLoading: totemsLoading } = useTotems();
  const { data: pendingCount = 0 } = usePendingAssignmentsCount();

  const availableCount = (stats?.available ?? 0) + (stats?.filled ?? 0);

  return (
    <PageContainer>
      <PageHeader
        title="Gestión de Totems"
        description="Seguimiento de contenedores retornables de 1000L"
        actions={
          <Button className="h-9" onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar Totem
          </Button>
        }
      />

      {/* Stats Cards */}
      <TotemStatsCards
        stats={
          stats || {
            available: 0,
            filled: 0,
            in_transit: 0,
            at_client: 0,
            pickup_ready: 0,
            pickup_scheduled: 0,
            pickup_transit: 0,
            returned: 0,
            cleaning: 0,
            damaged: 0,
            lost: 0,
            total: 0,
          }
        }
        pendingCount={pendingCount}
        isLoading={statsLoading}
      />

      {/* Shortfall alarm: totems invoiced while Disponible was empty/short */}
      {pendingCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Hay {pendingCount} asignación(es) de tótems pendientes por falta de
              disponibles al facturar. Muévelos al cliente cuando tengas stock.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 self-start sm:self-auto"
              onClick={() => setTab("pendientes")}
            >
              Ver pendientes
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="por-estado">Por Estado</TabsTrigger>
          <TabsTrigger value="por-cliente">Por Cliente</TabsTrigger>
          <TabsTrigger value="pendientes" className="gap-1.5">
            Pendientes
            {pendingCount > 0 && (
              <Badge
                variant="warning"
                className="h-5 min-w-[1.25rem] justify-center px-1"
              >
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent
          value="por-estado"
          className="space-y-6 duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          {/* Totems grouped by bucket ("bodega") */}
          <BucketTotemsTab
            totems={allTotems || []}
            isLoading={totemsLoading}
            onGoToClientView={() => setTab("por-cliente")}
          />
        </TabsContent>

        <TabsContent
          value="por-cliente"
          className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          <ClientTotemsTab totems={allTotems || []} isLoading={totemsLoading} />
        </TabsContent>

        <TabsContent
          value="pendientes"
          className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          <PendingAssignmentsTab availableCount={availableCount} />
        </TabsContent>

        <TabsContent
          value="historial"
          className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          <MovementsHistoryTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddTotemDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
    </PageContainer>
  );
}
